import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Database, Download, RefreshCw, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDateTimeWithZone } from '@/utils/dateUtils';
import DeviceStatusSlot, { useServicesUnavailable } from './DeviceStatusSlot';
import useDesktopObservation from '@/hooks/useDesktopObservation';
import useDesktopCommand from '@/hooks/useDesktopCommand';
import DeviceActionCard from './DeviceActionCard';
import SupportDetails from './SupportDetails';
import AdminConfirmationForm, { useAdminConfirmation } from './AdminConfirmationForm';
import { formatProgressBytes, orderDataProgressPresentation } from '@/utils/orderDataProgress';

const STATUS_UNAVAILABLE = Object.freeze({ state: 'status_unavailable', operationState: 'status_unavailable' });
const REBUILD_CONFIRMATION = 'REBUILD ORDER SNAPSHOT';
const ACTIVE_OPERATIONS = new Set([
  'checking',
  'downloading',
  'promoting',
  'resolving_invalidation',
  'saving',
  'verifying',
]);

function orderDataPresentation(status, loading, progress) {
  if (loading) return { label: 'Checking', detail: 'Reading this desktop’s order-data status.' };
  // TCA-26100807: an uncertain save cannot promise the old baseline survived.
  if (status?.operationReason === 'bootstrap_promotion_uncertain'
      || status?.operationValidationReason === 'transaction_outcome_uncertain') {
    return { label: 'Save needs checking',
      detail: 'The save result could not be confirmed. Do not rebuild again or clear saved transfer work until the result has been checked.' };
  }
  if (status?.operationReason === 'bootstrap_cleanup_pending') {
    return { label: 'Saved; cleanup pending',
      detail: 'The order snapshot was saved. Temporary-work cleanup still needs attention; do not rebuild just to clear it.' };
  }
  if (progress) return { label: progress.label, detail: progress.detail };
  if (status?.state === 'status_unavailable') return { label: 'Status unavailable', detail: 'Offline order status could not be established. Saved data has not been checked.' };
  switch (status?.operationState) {
    case 'checking':
      return { label: 'Checking', detail: 'Checking for newer online order activity.' };
    case 'downloading':
      return { label: 'Downloading', detail: 'Downloading newer online order data.' };
    case 'promoting':
      return { label: 'Saving', detail: 'Validating and installing the protected order baseline.' };
    case 'resolving_invalidation':
      return { label: 'Resolving large update', detail: 'Preparing a complete, consistent order update.' };
    case 'saving':
      return { label: 'Saving', detail: 'Saving accepted order changes to protected offline storage.' };
    case 'verifying':
      return { label: 'Verifying', detail: 'Comparing protected offline order data with a fresh cloud snapshot.' };
    case 'not_enabled':
      return {
        label: 'Not enabled',
        detail: 'Initial order history is available for installation to enable offline mode.',
      };
    case 'catching_up':
      return { label: 'Catching up', detail: 'More order changes remain and will continue on the next pass.' };
    case 'offline':
      return { label: 'Offline', detail: 'Online order checks are paused while this desktop is offline.' };
    case 'session_required':
      return { label: 'Sign-in required', detail: 'Sign in online on this desktop to resume order updates.' };
    case 'delayed':
    case 'failed':
      if (status?.operationReason === 'local_snapshot_promotion_response_invalid_json') {
        return {
          label: 'Local save failed',
          detail: status?.receiptAccepted
            ? 'This computer could not install the newest snapshot. Its previously accepted offline order data remains available.'
            : 'This computer downloaded order data but could not install its first protected offline baseline.',
        };
      }
      return {
        label: 'Delayed',
        detail: status?.receiptAccepted
          ? 'The latest check could not finish. Existing offline order data remains available.'
          : 'The first protected order-data download has not completed.',
      };
    case 'rebuild_required':
      return { label: 'Rebuild required', detail: 'The saved order-data position is no longer compatible with the cloud.' };
    case 'investigation_required':
      return {
        label: 'Investigation required',
        detail: status?.receiptAccepted
          ? 'A safety check found contradictory or invalid order data. Existing accepted data was preserved.'
          : 'The first protected order-data download was rejected before any offline baseline was accepted.',
      };
    default:
      if (status?.baselineState === 'ready' || status?.state === 'ready') {
        return { label: 'Current', detail: 'Offline order data is current as of the last completed check.' };
      }
      if (status?.state === 'desktop_unavailable') {
        return { label: 'Desktop app required', detail: 'Offline order-data controls are available in the desktop app.' };
      }
      return { label: 'Delayed', detail: 'The first protected order-data download has not completed.' };
  }
}

function verificationPresentation(verification) {
  if (!verification) return null;
  if (verification.result === 'matches_cloud') {
    return {
      label: 'Matches cloud',
      detail: 'This desktop’s protected order data matches the latest authoritative cloud snapshot.',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      icon: CheckCircle2,
    };
  }
  if (verification.result === 'does_not_match_cloud') {
    return {
      label: 'Does not match cloud',
      detail: 'The comparison found a difference. Existing offline data was preserved; investigate before rebuilding.',
      className: 'border-red-200 bg-red-50 text-red-900',
      icon: AlertTriangle,
    };
  }

  const reason = verification.reason;
  let label = 'Verification did not finish';
  let detail = 'Existing offline order data was preserved. Try again; if this repeats, contact support.';
  if (['catch_up_required', 'invalidation_resolution_required'].includes(reason)) {
    label = 'Finish catching up, then retry';
    detail = 'Run Check now to finish catching up, then verify again. Existing offline order data was preserved.';
  } else if (reason === 'order_data_operation_in_progress') {
    label = 'Try again shortly';
    detail = 'Another order-data operation is running. Verify again when it finishes. Existing offline order data was preserved.';
  } else if (reason === 'cloud_work_disabled') {
    label = 'Go online to verify';
    detail = 'Cloud verification needs an internet connection. Existing offline order data was preserved.';
  } else if (['cloud_session_required', 'active_administrator_required', 'cloud_session_changed'].includes(reason)) {
    label = 'Sign in to verify';
    detail = 'Go online and sign in as an administrator on this desktop, then verify again. Existing offline order data was preserved.';
  } else if (['accepted_baseline_required', 'missing_snapshot_receipt'].includes(reason)) {
    label = 'Offline data setup needed';
    detail = 'This desktop needs an accepted protected order baseline before it can be verified.';
  } else if ([
    'verification_response_invalid_shape',
    'verification_response_invalid_cohort',
    'verification_response_invalid_json',
    'verification_response_too_large',
    'snapshot_rejected',
    'comparison_integrity_error',
    'comparison_limit_exceeded',
    'generation_mismatch',
    'checkpoint_ahead',
  ].includes(reason)) {
    label = 'Verification needs investigation';
    detail = 'The comparison could not be trusted. Existing offline order data was preserved. Contact support before rebuilding.';
  }
  return {
    label,
    detail,
    className: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: AlertTriangle,
  };
}

const STATE_TONE = Object.freeze({
  delayed: 'amber', failed: 'amber', session_required: 'amber',
  rebuild_required: 'amber', investigation_required: 'red',
  checking: 'quiet', downloading: 'quiet', promoting: 'quiet', saving: 'quiet',
  resolving_invalidation: 'quiet', verifying: 'quiet', catching_up: 'quiet',
  offline: 'quiet', not_enabled: 'quiet',
});

export default function OrderDataCard({ shopTimezone = null }) {
  const observation = useDesktopObservation('getOrderDataStatus', 'onOrderDataStatusChanged');
  const { status: observedStatus, loading } = observation;
  const status = observedStatus || STATUS_UNAVAILABLE;
  const command = useDesktopCommand('order-data');
  const servicesUnavailable = useServicesUnavailable();
  const [showRebuild, setShowRebuild] = useState(false);
  const rebuildFields = useAdminConfirmation(REBUILD_CONFIRMATION);
  const [verificationFeedback, setVerificationFeedback] = useState(null);
  const [commandName, setCommandName] = useState(null);
  const checking = command.busy && commandName === 'check';
  const installing = command.busy && commandName === 'install';
  const rebuilding = command.busy && commandName === 'rebuild';
  const verifying = command.busy && commandName === 'verify';
  const { reset } = rebuildFields;
  useEffect(() => {
    reset(); setShowRebuild(false); setVerificationFeedback(null);
  }, [command.scope, reset]);
  const closeRebuild = () => { reset(); setShowRebuild(false); };
  const toggleRebuild = () => { reset(); setShowRebuild(value => !value); };
  const allowed = name => command.scope.current() && command.scope.admin && command.scope.cloud
    && observation.current() && !servicesUnavailable && observation.phase === 'ready' && !operationActive && !cloudUnavailable
    && (name !== 'rebuild' || !rebuildBlocked);

  const perform = async (name, method, payload) => {
    if (!allowed(name) || typeof command.scope.bridge?.[method] !== 'function') return;
    if (name === 'verify' && !verificationAvailable) return;
    if (name === 'rebuild' && (!showRebuild || !rebuildFields.complete)) return;
    const formCurrent = rebuildFields.capture();
    await command.run(bridge => {
      setCommandName(name); setVerificationFeedback(null);
      return payload === undefined ? bridge[method]() : bridge[method](payload);
    },
      async (result, error, current) => {
        if (error) {
          toast.error('The order-data outcome could not be confirmed. Check status before another attempt.');
          await observation.refresh();
          return;
        }
        const readback = await observation.refresh();
        if (!current()) return;
        if (name === 'verify') {
          // Successful evidence comes from the manager's current readback, never
          // an older command result merged into a newer baseline.
          if (result?.state === 'could_not_verify' && readback) setVerificationFeedback({
            status: readback, value: { result: 'could_not_verify', reason: result.reason || 'verification_failed' },
          });
          return;
        }
        if (name === 'check' || (name === 'rebuild' && !formCurrent())) return;
        if (result?.ok === true && result?.state === 'ready') {
          if (name === 'rebuild' && formCurrent()) closeRebuild();
          toast.success(name === 'rebuild' ? 'Offline order data rebuilt' : 'Offline order data installed');
        } else toast.error(result?.reason === 'order_data_operation_in_progress'
          ? `Another order-data task is running. Try ${name === 'rebuild' ? 'rebuilding' : 'installing'} again when it finishes.`
          : name === 'rebuild' ? 'Order-data rebuild did not finish' : 'Offline order data installation did not finish');
      });
  };
  const checkNow = () => perform('check', 'synchronizeOrderData');
  const installOfflineOrderData = () => perform('install', 'installOfflineOrderData');
  const rebuild = () => perform('rebuild', 'rebuildOrderData', rebuildFields.credentials);
  const verifyAgainstCloud = () => perform('verify', 'verifyOrderDataAgainstCloud');

  const progress = useMemo(
    () => orderDataProgressPresentation(status?.snapshotProgress),
    [status?.snapshotProgress],
  );
  const presentation = useMemo(
    () => orderDataPresentation(status, loading, progress),
    [status, loading, progress],
  );
  const verificationEvidence = verificationFeedback?.status === observedStatus ? verificationFeedback.value : status?.lastVerification;
  const verification = useMemo(
    () => verificationPresentation(verificationEvidence),
    [verificationEvidence],
  );
  const VerificationIcon = verification?.icon;
  const operationActive = (
    command.busy
    || checking
    || installing
    || rebuilding
    || verifying
    || Boolean(status?.activeOperation)
    || ACTIVE_OPERATIONS.has(status?.operationState)
  );
  const initialInstallActive = progress?.operation === 'bootstrap'
    && status?.receiptAccepted !== true;
  const notEnabled = (
    status?.operationState === 'not_enabled'
    || initialInstallActive
  ) && status?.receiptAccepted !== true;
  // Offline and a lapsed session both mean the cloud cannot be reached. Every
  // control on this tab needs it, so none of them may stay live while the status
  // above says those checks are paused. Offering an enabled "Check now" directly
  // beneath "Online order checks are paused" made the card contradict itself.
  //
  // The operation state is a lagging signal: it reports the last attempt, so a
  // card opened on an offline machine showed live controls until a check had
  // run and failed. The owner timed that window and clicked through it. The
  // connectivity the app already knows at render is checked first, so the
  // controls are fenced from the first paint (TCA-26100804 closeout).
  //
  // Read from the bridge rather than imported. An architecture guard in
  // `orderDataArchitecture.test.js` forbids any route-shaped path in this file
  // so the card cannot name a synchronization route, and the desktop adapter's
  // own module path trips it. This mirrors `isDesktopOffline` in that adapter,
  // and the card already reads the bridge directly everywhere else.
  const desktopOffline = Boolean(
    window.lvbDesktop?.localBackendUrl
    && window.lvbDesktop.backendUrl === window.lvbDesktop.localBackendUrl,
  );
  const cloudUnavailable = !command.scope.cloud || !command.scope.admin || servicesUnavailable
    || observation.phase !== 'ready' || desktopOffline
    || ['offline', 'session_required'].includes(status?.operationState);
  const rebuildBlocked = ['bootstrap_promotion_uncertain', 'bootstrap_cleanup_pending'].includes(status.operationReason)
    || status.operationValidationReason === 'transaction_outcome_uncertain';
  const verificationAvailable = (
    typeof window.lvbDesktop?.verifyOrderDataAgainstCloud === 'function'
    && (status?.baselineState === 'ready' || status?.state === 'ready')
    && !cloudUnavailable
  );

  return (
    <div className="space-y-3" data-testid="order-data-status">
      {command.uncertain && <p role="status">The order-data outcome is unknown. Check the native status or contact support before repeating the action.</p>}
      <DeviceStatusSlot
        tone={loading ? 'quiet' : STATE_TONE[status?.operationState] || (status?.operationState === 'idle' && status?.state === 'ready' ? 'green' : 'quiet')}
        title={presentation.label}
        detail={presentation.detail}
        testId="order-data-slot"
        titleTestId="order-data-status-label"
        detailTestId="order-data-status-detail"
      />

      {notEnabled && window.lvbDesktop?.installOfflineOrderData && (
        <DeviceActionCard
          icon={<Download className="h-4 w-4" />}
          title="Install offline order data"
          description="Download the protected order baseline this computer works from."
          testId="order-data-install-card"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={installOfflineOrderData}
            disabled={operationActive || cloudUnavailable}
            className="w-full h-9 text-xs"
            data-testid="order-data-install"
          >
            {installing || initialInstallActive ? 'Installing…' : 'Install offline order data'}
          </Button>
        </DeviceActionCard>
      )}

      {!notEnabled && (
        <DeviceActionCard
          icon={<RefreshCw className="h-4 w-4" />}
          title="Check for new orders"
          description="Download anything added online since the last check."
          testId="order-data-check-card"
        >
          <Button variant="outline" size="sm" onClick={checkNow} disabled={operationActive || cloudUnavailable || !window.lvbDesktop?.synchronizeOrderData} className="w-full h-9 text-xs" data-testid="order-data-check-now">
            Check now
          </Button>
        </DeviceActionCard>
      )}

      {!notEnabled && window.lvbDesktop?.verifyOrderDataAgainstCloud && (
        <DeviceActionCard
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Compare with online records"
          description="Check this computer's copy against the server without replacing it."
          testId="order-data-verify-card"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={verifyAgainstCloud}
            disabled={operationActive || !verificationAvailable}
            className="w-full h-9 text-xs"
            data-testid="order-data-verify"
          >
            {verifying || status?.operationState === 'verifying'
              ? 'Verifying…'
              : 'Verify against cloud'}
          </Button>
        </DeviceActionCard>
      )}

      {!notEnabled && window.lvbDesktop?.rebuildOrderData && (
        <DeviceActionCard
          icon={<Database className="h-4 w-4" />}
          title="Rebuild from scratch"
          description="Replace this computer's order data with a fresh download. Use when support asks."
          note="Administrator sign-in and a typed confirmation are required."
          risky
          testId="order-data-rebuild-card"
        >
          <Button variant="outline" size="sm" onClick={toggleRebuild} disabled={rebuilding || operationActive || cloudUnavailable || rebuildBlocked} className="w-full h-9 text-xs border-rose-200 text-rose-700 hover:text-rose-800" data-testid="order-data-rebuild-open">
            Rebuild data
          </Button>
        </DeviceActionCard>
      )}

      <SupportDetails
        testId="order-data-support-details"
        rows={[
          ['Baseline accepted', status?.receipt
            ? formatDateTimeWithZone(status.receipt.accepted_at, shopTimezone) || null : null,
          'order-data-accepted-at'],
          ['Orders', status?.receipt ? (Number.isSafeInteger(status.receipt.authoritative_count) ? String(status.receipt.authoritative_count) : null) : null,
            'order-data-order-count'],
          ['Ledger high-water', status?.receipt ? (status.receipt.high_water != null ? String(status.receipt.high_water) : null) : null,
            'order-data-high-water'],
          ['Last completed check', status?.lastSuccessfulSyncAt
            ? formatDateTimeWithZone(status.lastSuccessfulSyncAt, shopTimezone) || null : null,
          'order-data-last-sync'],
        ]}
      />

      {progress && (
        <div
          className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-950"
          data-testid="order-data-progress"
        >
          <div className="flex items-center justify-between gap-3 text-xs font-semibold">
            <span data-testid="order-data-progress-step">
              Step {progress.step} of {progress.totalSteps}
            </span>
            {progress.percent !== null && (
              <span data-testid="order-data-progress-percent">{progress.percent}%</span>
            )}
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-100"
            role="progressbar"
            aria-label={progress.label}
            {...(progress.percent === null
              ? { 'aria-valuetext': 'In progress' }
              : { 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': progress.percent })}
          >
            <div
              className={`h-full rounded-full bg-blue-600 ${progress.percent === null ? 'w-full animate-pulse' : ''}`}
              style={progress.percent === null ? undefined : { width: `${progress.percent}%` }}
              data-testid={progress.percent === null ? 'order-data-progress-indeterminate' : 'order-data-progress-determinate'}
            />
          </div>
          {progress.bytesReceived !== null && (
            <p className="mt-1.5 text-xs text-blue-800" data-testid="order-data-progress-bytes">
              {progress.bytesTotal === null
                ? `${formatProgressBytes(progress.bytesReceived)} accepted so far`
                : `${formatProgressBytes(progress.bytesReceived)} of ${formatProgressBytes(progress.bytesTotal)}`}
            </p>
          )}
          {progress.orderCount !== null && (
            <p className="mt-1.5 text-xs text-blue-800" data-testid="order-data-progress-orders">
              Snapshot contains {progress.orderCount} orders.
            </p>
          )}
          {progress.preservingBaseline && (
            <p className="mt-1.5 text-xs text-blue-800" data-testid="order-data-progress-preservation">
              Your previously accepted offline order data remains available until replacement succeeds.
            </p>
          )}
        </div>
      )}
      {verification && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 ${verification.className}`}
          data-testid="order-data-verification-result"
        >
          <div className="flex items-start gap-2">
            <VerificationIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold" data-testid="order-data-verification-label">
                {verification.label}
              </p>
              <p className="mt-0.5 text-xs" data-testid="order-data-verification-detail">
                {verification.detail}
              </p>
              {verificationEvidence.authoritative_count != null && (
                <p className="mt-1 text-xs opacity-80" data-testid="order-data-verification-evidence">
                  Cloud orders: {verificationEvidence.authoritative_count}
                  {verificationEvidence.local_count != null
                    ? ` · Offline orders: ${verificationEvidence.local_count}`
                    : ''}
                  {verificationEvidence.authoritative_high_water != null
                    ? ` · Ledger checkpoint: ${verificationEvidence.authoritative_high_water}`
                    : ''}
                </p>
              )}
              {verificationEvidence.shop_date && verificationEvidence.date_from && (
                <p className="mt-1 text-xs opacity-80" data-testid="order-data-verification-cohort">
                  Cloud shop date: {verificationEvidence.shop_date} · Cohort lower bound: {verificationEvidence.date_from}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {showRebuild && (
        <AdminConfirmationForm
          fields={rebuildFields}
          testIdPrefix="order-data-rebuild"
          className="mt-3 space-y-3 border-t border-stone-200 pt-3"
          instruction="This replaces the protected cloud-order baseline. Unsynchronized work created on this device is preserved. Sign in again as the currently active administrator and type the exact confirmation."
          confirmLabel="Replace order data"
          busyLabel="Rebuilding…"
          busy={rebuilding}
          disabled={operationActive || cloudUnavailable || rebuildBlocked}
          onConfirm={rebuild}
          onCancel={closeRebuild}
        />
      )}
    </div>
  );
}
